import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createStyles, makeStyles, MenuItem, Popover, PopoverOrigin, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import StaffMemberT, { PrimarySpecialty } from '../../../../../types/StaffMember';
import StaffMember from './StaffMember';
import AddStaffSlot from './AddStaffSlot';
import Spinner from '@/se/components/Spinner';
import StaffShiftMenuButton from './StaffShiftMenuButton';
import { useStaffMembersSubscription, useStaffShiftContext } from './StaffShiftContext';
import { light } from '../../../../../theme';
import { Procedure, ShiftState } from '@/types/Procedure';
import { useScope } from '@/hooks/useScope';
import { useStaffSlotContext } from './StaffSlotContext';
import { StaffShiftStoreContext, useCreateStaffShiftStore } from './StaffShiftChip';
import useDebounced from '../../../../../util/useDebounced';
import PrimarySpecialtyPicker from './PrimarySpecialtyPicker';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import clsx from 'clsx';

interface StaffShiftMenuItemProps {
  procedure?: Procedure;
  staffMember: StaffMemberT;
  toggleStaffMember: (staffMember: StaffMemberT) => (event: React.MouseEvent<HTMLElement>) => Promise<void>;
  editableStaff?: boolean;
  lightMode?: boolean;
  shiftType: 'RoomShift' | 'ProcedureShift';
}

const StaffShiftMenuItem: FC<StaffShiftMenuItemProps> = ({
  staffMember,
  procedure,
  toggleStaffMember,
  editableStaff,
  lightMode,
  shiftType,
}) => {
  const classes = useStyles({ lightMode: !!lightMode });
  const [busy, setBusy] = useState(false);

  const staffShiftStore = useCreateStaffShiftStore();

  const shiftState = staffMember.assignedShift?.shiftState ?? ShiftState.Available;

  const onClick = async (e: React.MouseEvent<HTMLElement>) => {
    if (!staffMember?.assignedShift?.id) {
      staffShiftStore.setState({ editTime: true });
    } else {
      staffShiftStore.setState({ editTime: false });
    }
    try {
      setBusy(true);
      await toggleStaffMember(staffMember)(e);
    } finally {
      setBusy(false);
    }
  };

  const debouncedBusy = useDebounced(busy, 1000) && busy;

  return (
    <MenuItem onKeyDown={e => e.stopPropagation()} key={staffMember.id} onClick={onClick}>
      <StaffShiftStoreContext.Provider value={staffShiftStore}>
        <StaffMember
          {...staffMember}
          busy={debouncedBusy}
          className={classes.staffMember}
          procedure={procedure}
          editableStaff={editableStaff}
          additionalText={staffMember?.additionalText}
          interactive
          isMenuListItem={true}
          shiftState={shiftState}
          type={shiftType}
          from={staffMember.assignedShift?.from}
          to={staffMember.assignedShift?.to}
        />
      </StaffShiftStoreContext.Provider>
    </MenuItem>
  );
};

interface StaffShiftMenuProps {
  procedure?: Procedure;
  defaultPrimarySpecialty?: PrimarySpecialty | null;
  lightMode?: boolean;
  maxVisibleStaff?: number;
  itemHeight?: number;
}

const DEFAULT_MAX_VISIBLE_STAFF = 10;
const DEFAULT_ITEM_HEIGHT = 56;

const StaffShiftMenu: FC<StaffShiftMenuProps> = ({
  procedure,
  defaultPrimarySpecialty,
  lightMode = false,
  maxVisibleStaff = DEFAULT_MAX_VISIBLE_STAFF,
  itemHeight = DEFAULT_ITEM_HEIGHT,
}) => {
  const classes = useStyles({ lightMode });

  const searchEl = React.useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [primarySpecialty, setPrimarySpecialty] = useState<PrimarySpecialty | null | undefined>(
    defaultPrimarySpecialty
  );
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const [maxRowsIncludingAddSlot, setMaxRowsIncludingAddSlot] = useState<number | null>(null);
  const [{ anchorOrigin, transformOrigin }, setPopoverOrigins] = useState<{
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
  }>(() => ({
    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    transformOrigin: { vertical: 'top', horizontal: 'center' },
  }));
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  const {
    activateSubscription,
    staffMembers,
    loading,
    primarySpecialty: contextPrimarySpecialty,
    setPrimarySpecialty: setContextPrimarySpecialty,
  } = useStaffMembersSubscription();

  useEffect(() => {
    if (primarySpecialty !== contextPrimarySpecialty) {
      setContextPrimarySpecialty(primarySpecialty);
    }
  }, [contextPrimarySpecialty, primarySpecialty, setContextPrimarySpecialty]);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    if (open) {
      searchEl.current?.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  const handleSearchUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    activateSubscription({ primarySpecialty });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const staffShiftContext = useStaffShiftContext();
  const staffSlotContext = useStaffSlotContext();
  const scope = useScope();

  const shiftType: 'RoomShift' | 'ProcedureShift' =
    'procedureId' in staffShiftContext.scope ? 'ProcedureShift' : 'RoomShift';

  const addSlot = async () => {
    await staffShiftContext.add();
    staffSlotContext.openOpenPositionDialog();
    handleClose();
  };

  const toggleStaffMember = (staffMember: StaffMemberT) => async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (staffMember.assignedShift) {
      await staffShiftContext.remove(staffMember.assignedShift.id, scope?.hospital?.id, scope?.hospital?.id);
    } else {
      await staffShiftContext.add(staffMember?.staffId, staffMember?.hourlyRate);
    }
  };

  const filteredStaffMembers = useMemo(() => {
    const chunks = search.toLowerCase().split(/\s+/);
    return (staffMembers || []).filter((staffMember: StaffMemberT) => {
      const staffMemberName = staffMember.name.toLowerCase();
      return chunks.every(chunk => staffMemberName.indexOf(chunk) >= 0);
    });
  }, [search, staffMembers]);

  const recalculateLayout = useCallback(() => {
    if (!anchorEl) {
      return;
    }

    const margin = 16;
    const rect = anchorEl.getBoundingClientRect();
    const availableBelow = window.innerHeight - rect.bottom - margin;
    const availableAbove = rect.top - margin;
    const preferBelow = availableBelow >= availableAbove;
    const availableSpace = Math.max(preferBelow ? availableBelow : availableAbove, itemHeight);
    setPopoverOrigins({
      anchorOrigin: {
        vertical: preferBelow ? 'bottom' : 'top',
        horizontal: 'center',
      },
      transformOrigin: {
        vertical: preferBelow ? 'top' : 'bottom',
        horizontal: 'center',
      },
    });

    const controlsHeight = controlsRef.current?.offsetHeight ?? 0;
    const usableSpace = Math.max(availableSpace - controlsHeight, itemHeight);
    const rowsIncludingAddSlot = Math.max(1, Math.floor(usableSpace / itemHeight));
    const cappedRows = Math.min(rowsIncludingAddSlot, maxVisibleStaff + 1);
    setMaxRowsIncludingAddSlot(cappedRows);
  }, [anchorEl, itemHeight, maxVisibleStaff]);

  useEffect(() => {
    if (!open) {
      return;
    }

    recalculateLayout();
    window.addEventListener('resize', recalculateLayout);
    return () => {
      window.removeEventListener('resize', recalculateLayout);
    };
  }, [open, recalculateLayout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: 0, align: 'start' });
    });
  }, [open, filteredStaffMembers.length]);

  const totalItems = filteredStaffMembers.length + 1;
  const rowsCap = maxRowsIncludingAddSlot ?? maxVisibleStaff + 1;
  const visibleRows = Math.min(rowsCap, Math.max(1, totalItems));
  const virtualListHeight = itemHeight * visibleRows;
  const isAnchoredUp = anchorOrigin.vertical === 'top';

  const controls = (
    <div ref={controlsRef} className={clsx(classes.controlsContainer, { [classes.controlsReverse]: isAnchoredUp })}>
      {primarySpecialty !== undefined && (
        <PrimarySpecialtyPicker
          value={primarySpecialty}
          onChange={ps => {
            setPrimarySpecialty(ps);
            setContextPrimarySpecialty(ps);
            activateSubscription({ primarySpecialty: ps });
          }}
        />
      )}
      <TextField
        size="small"
        variant="outlined"
        value={search}
        onChange={handleSearchUpdate}
        inputRef={searchEl}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" className={classes.searchIcon} />,
          autoCapitalize: 'off',
          autoComplete: 'off',
          autoCorrect: 'off',
          autoFocus: true,
          classes: {
            root: classes.inputRoot,
            inputAdornedStart: classes.inputAdornedStart,
          },
        }}
        classes={{
          root: classes.textFieldRoot,
        }}
      />
    </div>
  );

  return (
    <>
      <StaffShiftMenuButton onClick={handleButtonClick} />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClick={e => e.stopPropagation()}
        onClose={handleClose}
        getContentAnchorEl={null}
        keepMounted
        classes={{
          paper: classes.popover,
        }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <div className={classes.contentContainer} onClick={event => event.stopPropagation()}>
          {!isAnchoredUp && controls}
          {staffMembers ? (
            <div className={classes.virtualListContainer} role="menu">
              <Virtuoso
                ref={virtuosoRef}
                style={{ height: virtualListHeight, width: '100%' }}
                totalCount={totalItems}
                defaultItemHeight={itemHeight}
                computeItemKey={index =>
                  index === 0 ? 'add-slot' : (filteredStaffMembers[index - 1]?.id ?? `staff-${index}`)
                }
                itemContent={index => {
                  if (index === 0) {
                    return (
                      <MenuItem onClick={addSlot}>
                        <AddStaffSlot interactive />
                      </MenuItem>
                    );
                  }

                  const staffMember = filteredStaffMembers[index - 1];

                  return (
                    <StaffShiftMenuItem
                      key={staffMember.id}
                      staffMember={staffMember}
                      procedure={procedure}
                      toggleStaffMember={toggleStaffMember}
                      editableStaff={true}
                      lightMode={lightMode}
                      shiftType={shiftType}
                    />
                  );
                }}
              />
            </div>
          ) : (
            <div className={classes.spinnerContainer}>
              <Spinner freezeAnimation={!open || !loading} className={classes.spinner} />
            </div>
          )}
          {isAnchoredUp && controls}
        </div>
      </Popover>
    </>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    textFieldRoot: {
      margin: '1rem',
      marginBottom: 0,
      padding: 0,
    },
    inputRoot: {
      width: '100%',
      fontSize: '0.8125em',
      padding: '0.25em',
      color: 'white',
    },
    searchIcon: {
      marginLeft: '0.25em',
      marginRight: '0.25em',
    },
    inputAdornedStart: {
      paddingLeft: 0,
    },
    spinnerContainer: {
      padding: '1rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    spinner: {},
    staffMember: {
      flex: 1,
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      width: '24rem',
      maxWidth: '24rem',
      boxSizing: 'border-box',
    },
    controlsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: 0,
    },
    controlsReverse: {
      flexDirection: 'column-reverse',
    },
    virtualListContainer: {
      width: '100%',
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
    },
    popover: (props: { lightMode: boolean }) =>
      props.lightMode ? {} : { backgroundColor: light.popover.background.string() },
  })
);
export default StaffShiftMenu;
